import React, { useCallback, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { X, Loader2, FileText, Image, FileSpreadsheet } from "lucide-react";

export interface AttachedFile {
  fileId: string;
  originalName: string;
  charCount: number;
  mimeType?: string;
  filePath?: string;
}

export interface FileDropZoneRef {
  openFilePicker: () => void;
}

interface FileDropZoneProps {
  /** Whether a drag-over overlay should be shown */
  isDragOver: boolean;
  /** Called when files are dropped */
  onDrop: (files: FileList) => void;
  /** Called when drag enters the zone */
  onDragEnter: (e: React.DragEvent) => void;
  /** Called when drag leaves the zone */
  onDragLeave: (e: React.DragEvent) => void;
  /** Called when drag is over the zone */
  onDragOver: (e: React.DragEvent) => void;
  /** Currently attached files (chips) */
  attachedFiles: AttachedFile[];
  /** Remove an attached file */
  onRemoveFile: (fileId: string) => void;
  /** Whether an upload is in progress */
  uploading: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ["pdf", "csv", "xlsx", "docx", "txt", "png", "jpg", "jpeg"];
const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg|avif|jfif)$/i;

const getFileIcon = (mimeType?: string) => {
  if (!mimeType) return <FileText size={14} className="text-gray-400" />;
  if (mimeType.startsWith("image/")) return <Image size={14} className="text-purple-500" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("csv") || mimeType.includes("xlsx"))
    return <FileSpreadsheet size={14} className="text-emerald-500" />;
  return <FileText size={14} className="text-blue-500" />;
};

const hasImageExtension = (value?: string) =>
  IMAGE_FILE_PATTERN.test(String(value || "").split("?")[0]);

const isImageFile = (file: AttachedFile) => {
  if (!file.filePath) return false;
  if (file.mimeType?.startsWith("image/")) return true;
  return hasImageExtension(file.filePath) || hasImageExtension(file.originalName);
};

const getUploadUrl = (filePath?: string) => {
  if (!filePath) return "";
  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  return `${baseUrl}/public/uploads/${filePath}`;
};

const FileDropZone = forwardRef<FileDropZoneRef, FileDropZoneProps>(({
  isDragOver,
  onDrop,
  onDragEnter,
  onDragLeave,
  onDragOver,
  attachedFiles,
  onRemoveFile,
  uploading,
}, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    openFilePicker: () => {
      fileInputRef.current?.click();
    },
  }));

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onDrop(e.target.files);
      // Reset the input so the same file can be re-selected
      e.target.value = "";
    }
  };

  return (
    <>
      {/* Drag overlay — shown only when dragging a file over the input area */}
      {isDragOver && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-[#0C447C] bg-[#0C447C]/5 pointer-events-none"
          aria-hidden="true"
        >
          <div className="flex flex-col items-center gap-2 text-[#0C447C]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.49" />
            </svg>
            <span className="text-sm font-semibold">Drop file here</span>
            <span className="text-[10px] text-gray-500">
              PDF, CSV, XLSX, DOCX, TXT, PNG, JPG — max 10MB
            </span>
          </div>
        </div>
      )}

      {/* Upload progress indicator */}
      {uploading && !isDragOver && (
        <div className="flex items-center gap-2 px-4 py-1.5">
          <Loader2 size={14} className="animate-spin text-[#0C447C]" />
          <span className="text-xs text-[#0C447C] font-medium">Uploading file…</span>
        </div>
      )}

      {/* Attached file previews */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 px-4 pt-2">
          {attachedFiles.map((file) => {
            if (isImageFile(file)) {
              return (
                <div
                  key={file.fileId}
                  className="relative h-24 w-24 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm group/preview"
                  title={file.originalName}
                >
                  <img
                    src={getUploadUrl(file.filePath)}
                    alt={file.originalName}
                    className="h-full w-full object-cover"
                  />
                  <button
                    onClick={() => onRemoveFile(file.fileId)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-90 transition hover:bg-red-500"
                    aria-label={`Remove ${file.originalName}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            }

            return (
              <div
                key={file.fileId}
                className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs group/chip"
              >
                {getFileIcon(file.mimeType)}
                <span className="text-gray-700 font-medium max-w-[120px] truncate" title={file.originalName}>
                  {file.originalName}
                </span>
                <button
                  onClick={() => onRemoveFile(file.fileId)}
                  className="text-gray-400 hover:text-red-500 transition-colors ml-0.5"
                  aria-label={`Remove ${file.originalName}`}
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden file input for click-to-attach */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.csv,.xlsx,.docx,.txt,.png,.jpg,.jpeg"
        className="hidden"
        onChange={handleFileInputChange}
        aria-label="Attach a file"
      />
    </>
  );
});

FileDropZone.displayName = "FileDropZone";

export { MAX_FILE_SIZE, ALLOWED_EXTENSIONS };
export default FileDropZone;
